// resultScoreUtils.js
// Pure, reusable helpers for turning raw score/range fields from the
// submit-test API into safe numbers, percentages, and display strings.
//
// Root-cause context (see TestResults.jsx header comment for the full
// writeup): the previous implementation computed the overall percentage as
// `totalScore / testTotalScore`. That field name doesn't match anything the
// API sends, and on the submit-test endpoint there's no total at all, so
// that calculation always bailed out to `null` and the score UI silently
// disappeared. The fallback the app used instead is the applicable min/max
// range that ships alongside each score (`minInterpretationValue`/
// `maxInterpretationValue` for the overall score, `minSecInterValue`/
// `maxSecInterValue` per section).
//
// UPDATE: the history endpoint (`tests/results/{userId}`) now sends real
// totals — `totalTestScore` per result and `totalSectionScore` per section —
// so `score / total` is now the primary percentage source wherever it's
// present and non-zero (calculateScoreFractionPercentage), with the min/max
// range calc (calculateScorePercentage) kept as a fallback for sources that
// still only send a range (or a `totalTestScore`/`totalSectionScore` of 0,
// which shows up on a couple of legacy/duplicated tests).
//
// Every helper below is written to be defensive about null/undefined/NaN
// input so a bad or partial API payload can never produce a runtime error,
// a NaN in the UI, or a broken chart.

/**
 * Coerce a value to a finite number, or null if it can't be.
 * Treats "", null, undefined, NaN, Infinity as "no number".
 */
export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/** Clamp a number between min and max (defaults to a 0-100 percentage). */
export const clamp = (value, min = 0, max = 100) => {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
};

/**
 * Position of `score` within [min, max], expressed as a 0-100 percentage.
 * This represents *where the score falls in the range*, not `score/max`.
 * e.g. score=1, min=1, max=6  ->  0%   (score sits at the very bottom)
 *      score=6, min=1, max=6  -> 100%  (score sits at the very top)
 *
 * Handles: null/undefined score or range, non-numeric input, min === max
 * (degenerate range), and scores that fall outside [min, max] (clamped).
 * Returns null when the percentage genuinely cannot be computed, so callers
 * can distinguish "no data" from "0%".
 */
export const calculateScorePercentage = (score, min, max) => {
  const s = toFiniteNumber(score);
  const mn = toFiniteNumber(min);
  const mx = toFiniteNumber(max);
  if (s === null || mn === null || mx === null) return null;
  if (mx === mn) return s >= mx ? 100 : 0;

  // Support ranges given in either order (min > max would otherwise divide
  // by a negative number and invert the bar).
  const lo = Math.min(mn, mx);
  const hi = Math.max(mn, mx);
  const pct = ((s - lo) / (hi - lo)) * 100;
  return Math.round(clamp(pct, 0, 100));
};

/**
 * "True" percentage from score/total (e.g. totalScore/totalTestScore,
 * sectionScore/totalSectionScore), for payloads that provide a real total
 * rather than just an interpretation min/max range.
 *
 * Returns null — never NaN/Infinity — when score or total is missing, or
 * when total is <= 0 (some real payloads carry `totalTestScore`/
 * `totalSectionScore: 0`, e.g. a duplicated "(Copy)" test, which would
 * otherwise divide by zero). Callers should fall back to
 * calculateScorePercentage's range-based result when this returns null.
 */
export const calculateScoreFractionPercentage = (score, total) => {
  const s = toFiniteNumber(score);
  const t = toFiniteNumber(total);
  if (s === null || t === null || t <= 0) return null;
  return Math.round(clamp((s / t) * 100, 0, 100));
};

/**
 * "score / max" style fraction string, or null if either side is missing.
 * A max of 0 is also treated as "no usable total" — a "3 / 0" fraction is
 * never meaningful, and a 0 total shows up on a couple of legacy/
 * duplicated tests in real payloads.
 */
export const formatScoreFraction = (score, max) => {
  const s = toFiniteNumber(score);
  const mx = toFiniteNumber(max);
  if (s === null || mx === null || mx === 0) return null;
  return `${s} / ${mx}`;
};

/** "min – max" range string, or null if either side is missing. */
export const formatRange = (min, max) => {
  const mn = toFiniteNumber(min);
  const mx = toFiniteNumber(max);
  if (mn === null || mx === null) return null;
  return `${Math.min(mn, mx)} – ${Math.max(mn, mx)}`;
};

/** Always returns an array, never throws on null/undefined/non-array input. */
export const normalizeSections = (sections) =>
  Array.isArray(sections) ? sections.filter(Boolean) : [];

/**
 * Resolve a visibility flag with this precedence:
 *   1. explicit prop passed to <TestResults />
 *   2. same-named flag embedded in the API result payload
 *   3. default (true) — never hide a section of the UI just because a flag
 *      is missing; only hide it when something explicitly says `false`.
 */
export const resolveVisibility = (propValue, dataValue, fallback = true) => {
  if (propValue !== undefined && propValue !== null) return propValue !== false;
  if (dataValue !== undefined && dataValue !== null) return dataValue !== false;
  return fallback;
};

/**
 * The submit-test API response is sometimes the full envelope
 * ({ status, message, data }) and sometimes just the inner `data` object,
 * depending on where in the app it's threaded through. Accept both so the
 * results screen doesn't break if that wiring changes upstream.
 *
 * Field-name note: the history endpoint (`tests/results/{userId}`) nests the
 * overall interpretation/recommendation as objects —
 * `interpretation: { title, description, minScore, maxScore }` and
 * `recommendation: { title, recommendationText, minScore, maxScore }` —
 * instead of sending flat `interpretationTitle`/`interpretationDescription`/
 * `recommendationTitle`/`recommendationDescription`/min-max fields directly
 * on the result object the way the submit-test response does. Left
 * unnormalized, only whichever piece happens to get hand-mapped elsewhere
 * (e.g. just the description) shows up, and the title silently disappears.
 * This flattens both shapes here so the rest of the app only ever has to
 * read the flat field names, regardless of which endpoint the data came
 * from. `??` (not `||`) is used so a legitimate empty string (seen in real
 * payloads, e.g. `interpretation.title: ""`) isn't overwritten by the
 * fallback — the title just falls through to the component's own
 * "Your Overview" / "Recommendations for Growth" default in that case.
 */
export const extractResultData = (results) => {
  if (!results || typeof results !== "object") return null;
  const raw =
    results.data && typeof results.data === "object" ? results.data : results;
  if (!raw || typeof raw !== "object") return raw;

  const interp = raw.interpretation;
  const rec = raw.recommendation;

  if (!interp && !rec) return raw;

  return {
    ...raw,
    interpretationTitle: raw.interpretationTitle ?? interp?.title ?? null,
    interpretationDescription:
      raw.interpretationDescription ?? interp?.description ?? null,
    minInterpretationValue:
      raw.minInterpretationValue ?? interp?.minScore ?? null,
    maxInterpretationValue:
      raw.maxInterpretationValue ?? interp?.maxScore ?? null,
    recommendationTitle: raw.recommendationTitle ?? rec?.title ?? null,
    recommendationDescription:
      raw.recommendationDescription ?? rec?.recommendationText ?? null,
    minRecommendationValue: raw.minRecommendationValue ?? rec?.minScore ?? null,
    maxRecommendationValue: raw.maxRecommendationValue ?? rec?.maxScore ?? null,
  };
};

/**
 * Build the small set of derived numbers/strings a section card needs,
 * all null-safe. Nothing here throws on missing fields.
 *
 * `index` is used only as a display-title fallback (some real payloads —
 * e.g. the user-history API — return sections with an empty-string title).
 *
 * Field-name note: the submit-test API names the section recommendation
 * body `sectionRecommendationDescription`; the user-history API names the
 * same thing `sectionRecommendationText`. Both are accepted here so this
 * helper works no matter which endpoint the caller mapped from.
 *
 * Total/range note: the history API (`tests/results/{userId}`) now sends a
 * real `totalSectionScore` alongside `sectionScore`, so `pct` is computed
 * as score/totalSectionScore first. Some other sources (or a totalSectionScore
 * of 0, seen on a couple of legacy/duplicated tests) don't give a usable
 * total, in which case this falls back to position-in-range against
 * `minSecInterValue`/`maxSecInterValue` if that's present. When neither is
 * available, `pct` and `fraction` come back `null` and the caller should
 * fall back to showing the raw `score` instead of a percentage/progress bar.
 */
export const buildSectionView = (section, index = 0) => {
  const score = toFiniteNumber(section?.sectionScore);
  const total = toFiniteNumber(section?.totalSectionScore);
  const min = toFiniteNumber(section?.minSecInterValue);
  const max = toFiniteNumber(section?.maxSecInterValue);

  const fractionPct = calculateScoreFractionPercentage(score, total);
  const rangePct = calculateScorePercentage(score, min, max);
  const pct = fractionPct !== null ? fractionPct : rangePct;

  const recMin = toFiniteNumber(section?.minSecRecValue);
  const recMax = toFiniteNumber(section?.maxSecRecValue);
  const recPct = calculateScorePercentage(score, recMin, recMax);

  const rawTitle = section?.sectionTitle;
  const title =
    typeof rawTitle === "string" && rawTitle.trim().length > 0
      ? rawTitle.trim()
      : `Section ${index + 1}`;

  return {
    id: section?.sectionId ?? index,
    title,
    score,
    total,
    min,
    max,
    pct, // 0-100 or null — null means "no total or range available, show raw score"
    fraction: formatScoreFraction(score, total ?? max),
    range: formatRange(min, max),
    interpretationTitle: section?.sectionInterpretationTitle || null,
    interpretationDescription:
      section?.sectionInterpretationDescription || null,
    recommendationTitle: section?.sectionRecommendationTitle || null,
    recommendationDescription:
      section?.sectionRecommendationDescription ||
      section?.sectionRecommendationText ||
      null,
    recPct,
    recRange: formatRange(recMin, recMax),
  };
};
